import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { loadSession } from "../auth/authStorage";
import { useLatestRequestController } from "../hooks/useLatestRequestController";
import { getData, getDataTraining, jsonPostData } from "../util/api-calls";
import { handleError } from "../util/error-handler";

type GenericObject = Record<string, unknown>;
type SportOption = { id: number; name: string };

export type UserContextValue = {
  userAccessToken: string | null;
  setUserAccessToken: React.Dispatch<React.SetStateAction<string | null>>;
  setUser: React.Dispatch<React.SetStateAction<GenericObject | null>>;
  accountDetail: GenericObject;
  selectedSport: number | undefined;
  darkButtonBackground: string;
  color: string;
  border: string;
  lightButtonBackground: string;
  sportsList: SportOption[];
  postSelectedSport: (id: number) => void;
  allTags: unknown[];
  notificationData: unknown[];
  setNotificationData: React.Dispatch<React.SetStateAction<unknown[]>>;
  notificationCount: number;
  setNotificationCount: React.Dispatch<React.SetStateAction<number>>;
  selectedNotification: unknown;
  setSelectedNotification: React.Dispatch<React.SetStateAction<unknown>>;
  notificationProfileId: string;
  setNotificationProfileId: React.Dispatch<React.SetStateAction<string>>;
  getAllTags: (category: string) => void;
  notificationTitle: string;
  setNotificationTitle: React.Dispatch<React.SetStateAction<string>>;
  broadcastModal: boolean;
  setBroadcastModal: React.Dispatch<React.SetStateAction<boolean>>;
  searchModal: boolean;
  setSearchModal: React.Dispatch<React.SetStateAction<boolean>>;
  selectedDate: unknown;
  setSelectedDate: React.Dispatch<React.SetStateAction<unknown>>;
};

export const UserContext = React.createContext<UserContextValue | null>(null);

export const UserContextProvider = ({ children }: { children?: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [userAccessToken, setUserAccessToken] = React.useState<string | null>(null);
  const [user, setUser] = React.useState<GenericObject | null>(null);
  const [accountDetail, setAccountDetail] = useState<GenericObject>({});
  const [selectedSport, setSelectedSport] = useState<number>();
  const [darkButtonBackground, setDarkButtonBackground] = useState("");
  const [lightButtonBackground, setLightButtonBackground] = useState("");
  const [sportsList, setSportsList] = useState<SportOption[]>([]);
  const [allTags, setAllTags] = useState<unknown[]>([]);
  const [color, setColor] = useState("");
  const [border, setBorder] = useState("");
  const [notificationData, setNotificationData] = useState<unknown[]>([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState<unknown>();
  const [notificationProfileId, setNotificationProfileId] = useState("");
  const [notificationTitle, setNotificationTitle] = useState("");
  const [broadcastModal, setBroadcastModal] = useState(false);
  const [searchModal, setSearchModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<unknown>(null);

  const tagsReq = useLatestRequestController();

  const getAllTags = useCallback(
    (category: string) => {
      const c = tagsReq.begin();
      getDataTraining(`sport/${selectedSport ? selectedSport : 1}/tags?category=${category}`, {
        signal: c.signal,
      })
        .then((response: { data: unknown[] }) => {
          if (!tagsReq.isCurrent(c)) return;
          setAllTags(response.data);
        })
        .catch((error: unknown) => {
          if (!tagsReq.isCurrent(c)) return;
          handleError(error, { context: "src/context/user-context.tsx" });
        });
    },
    [selectedSport, tagsReq],
  );

  const hasAuthenticatedSession = () => {
    const session = loadSession();
    return Boolean(session?.access_token);
  };

  const postSelectedSport = (id: number) => {
    jsonPostData("user/sports/switch", { sport_id: id })
      .then(() => {
        setSelectedSport(id);
        navigate("/home/teams", { replace: true });
      })
      .catch((error: unknown) => {
        handleError(error, { context: "src/context/user-context.tsx" });
      });
  };

  useEffect(() => {
    setDarkButtonBackground("#F15A29");
    setLightButtonBackground("#FFF1ED");
    setColor("#F15A29");
    setBorder("#F15A29");
    if (!hasAuthenticatedSession()) return undefined;
    if (!location.pathname.startsWith("/home")) return undefined;

    const id = user?.id ?? localStorage.getItem("u_id");
    if (id == null || id === "") return undefined;

    const cAcc = new AbortController();
    const cSports = new AbortController();

    getData(`admin/user/account/${id}`, {}, { signal: cAcc.signal })
      .then((response: { data: GenericObject & { profile?: { selected_sport?: SportOption } } }) => {
        setAccountDetail(response.data);
        const selected = response.data?.profile?.selected_sport;
        if (!selected || selected.name === "") {
          setSelectedSport(1);
        } else {
          setSelectedSport(selected.id);
        }
      })
      .catch((error: unknown) => {
        handleError(error, { context: "src/context/user-context.tsx" });
      });

    getData("sport?all_sports=true", {}, { signal: cSports.signal })
      .then((response: { data: SportOption[] }) => {
        setSportsList(response.data);
      })
      .catch((error: unknown) => {
        handleError(error, { context: "src/context/user-context.tsx" });
      });

    return () => {
      cAcc.abort();
      cSports.abort();
    };
  }, [location.pathname, user]);

  const defaultContext: UserContextValue = {
    userAccessToken,
    setUserAccessToken,
    setUser,
    accountDetail,
    selectedSport,
    darkButtonBackground,
    color,
    border,
    lightButtonBackground,
    sportsList,
    postSelectedSport,
    allTags,
    notificationData,
    setNotificationData,
    notificationCount,
    setNotificationCount,
    selectedNotification,
    setSelectedNotification,
    notificationProfileId,
    setNotificationProfileId,
    getAllTags,
    notificationTitle,
    setNotificationTitle,
    broadcastModal,
    setBroadcastModal,
    searchModal,
    setSearchModal,
    selectedDate,
    setSelectedDate,
  };

  return <UserContext.Provider value={defaultContext}>{children}</UserContext.Provider>;
};
